import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AggregateState } from './aggregate.state';
import { selectFilter } from '../../common/state/alert/alert.selectors';
import { buildAlertFilterGroups, FilterGroup, IFilter } from '../../modules/alerts/filter';
import {
  AggregateSpec,
  AlertAggregateRequest,
  DETECTION_IN_FILTER_KEYS,
  DETECTION_RANGE_FILTER_KEYS,
  DrilldownStep,
  GROUP_BY_LABEL_MAP,
} from '../models/aggregate.models';

function toStringArray(v: any): string[] {
  if (Array.isArray(v)) {
    return v.map(String);
  }
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) {
        return parsed.map(String);
      }
    } catch {}
    return [v];
  }
  return [String(v)];
}

function buildDrilldownGroups(drilldownPath: DrilldownStep[]): FilterGroup[] {
  const groups: FilterGroup[] = [];
  const alertSteps = drilldownPath.filter((s) => !s.target);
  const detectionSteps = drilldownPath.filter((s) => s.target === 'detections');
  if (alertSteps.length > 0) {
    groups.push({
      logic: 'AND' as const,
      filters: alertSteps.map((step) => ({
        key: step.key,
        operator: '==' as const,
        value: step.value,
        ...(step.truncate ? { truncate: step.truncate } : {}),
      } as IFilter)),
    });
  }
  // Skip the detection group if any step has a null value — API has no IS_NULL operator.
  if (detectionSteps.length > 0 && detectionSteps.every((s) => s.value != null)) {
    groups.push({
      logic: 'AND' as const,
      filters: detectionSteps.flatMap((step) => {
        // Integer-cast fields: GROUP BY uses floor(n), so drilldown must cover [n, n+1)
        if (DETECTION_RANGE_FILTER_KEYS.has(step.key)) {
          return [
            { key: step.key, operator: '>=' as const, value: Number(step.value) } as IFilter,
            { key: step.key, operator: '<' as const, value: Number(step.value) + 1 } as IFilter,
          ];
        }
        // Enum fields use IN with a string[] value
        if (DETECTION_IN_FILTER_KEYS.has(step.key)) {
          return [{ key: step.key, operator: 'IN' as const, value: toStringArray(step.value) } as IFilter];
        }
        return [{ key: step.key, operator: '==' as const, value: step.value } as IFilter];
      }),
      target: 'detections' as const,
    });
  }
  return groups;
}

export const selectAggregateState = createFeatureSelector<AggregateState>('aggregate');

export const selectIsAggregateActive = createSelector(
  selectAggregateState,
  (state) => state.active,
);

export const selectAggregatePendingChanges = createSelector(
  selectAggregateState,
  (state) => state.hasPendingChanges,
);

export const selectMetricsHavingModified = createSelector(
  selectAggregateState,
  (state) => state.metricsHavingModified,
);

export const selectGroupBy = createSelector(
  selectAggregateState,
  (state) => state.groupBy,
);

export const selectCommittedGroupBy = createSelector(
  selectAggregateState,
  (state) => state.committedGroupBy,
);

export const selectAggregateSpecs = createSelector(
  selectAggregateState,
  (state) => state.aggregates,
);

export const selectHaving = createSelector(
  selectAggregateState,
  (state) => state.having,
);

export const selectOrdering = createSelector(
  selectAggregateState,
  (state) => state.ordering,
);

export const selectAggregatePagination = createSelector(
  selectAggregateState,
  (state) => state.pagination,
);

export const selectDrilldownPath = createSelector(
  selectAggregateState,
  (state) => state.drilldownPath,
);

export const selectSelectedGroups = createSelector(
  selectAggregateState,
  (state) => state.selectedGroups,
);

export const selectLeafGroupByLabel = createSelector(
  selectAggregateState,
  (state): string | null => {
    const lastStep = state.drilldownPath[state.drilldownPath.length - 1];
    if (!lastStep) {
      return null;
    }
    return GROUP_BY_LABEL_MAP[lastStep.key] ?? lastStep.key;
  },
);

export const selectIsAtLeafLevel = createSelector(
  selectAggregateState,
  (state) => state.committedGroupBy.length > 0 && state.drilldownPath.length >= state.committedGroupBy.length,
);

export const selectCurrentGroupByKey = createSelector(
  selectAggregateState,
  (state) => state.committedGroupBy[state.drilldownPath.length]?.key ?? null,
);

export const selectNextGroupByKey = createSelector(
  selectAggregateState,
  (state) => state.committedGroupBy[state.drilldownPath.length + 1]?.key ?? null,
);

export const selectAggregateResults = createSelector(
  selectAggregateState,
  (state) => state.results,
);

export const selectAggregateLoading = createSelector(
  selectAggregateState,
  (state) => state.loading,
);

export const selectAggregateError = createSelector(
  selectAggregateState,
  (state) => state.error,
);

export const selectAggregateSuccess = createSelector(
  selectAggregateState,
  (state) => state.success,
);


export const selectAggregateTotalGroups = createSelector(
  selectAggregateState,
  (state) => state.totalGroups,
);

export const selectAggregateHasNextPage = createSelector(
  selectAggregateState,
  (state) => state.pagination.page_num < state.totalPages,
);

export const selectAggregateIsAppending = createSelector(
  selectAggregateState,
  (state) => state.isAppending,
);

export const selectTotalAlertCount = createSelector(
  selectAggregateState,
  (state) => {
    const countAlias = state.aggregates.find((a) => a.function === 'COUNT')?.alias;
    if (!countAlias) {
      return null;
    }
    return state.results.reduce((sum, row) => sum + (Number(row[countAlias]) || 0), 0);
  },
);

// Sum of the COUNT metric across only the checkbox-selected groups, so a scoped
// report's alert_count matches its filter set. Null when nothing is selected
// (callers fall back to the full total).
export const selectSelectedGroupsAlertCount = createSelector(
  selectAggregateState,
  (state) => {
    const countAlias = state.aggregates.find((a) => a.function === 'COUNT')?.alias;
    if (!countAlias || state.selectedGroups.length === 0) {
      return null;
    }
    return state.selectedGroups.reduce((sum, group) => {
      const row = state.results.find((r) => r[group.key] === group.value);
      return sum + (Number(row?.[countAlias]) || 0);
    }, 0);
  },
);

export const selectAggregateRequest = createSelector(
  selectAggregateState,
  selectFilter,
  (aggState, filter): AlertAggregateRequest => {
    const groups = buildAlertFilterGroups(filter);

    groups.push(...buildDrilldownGroups(aggState.drilldownPath));

    const depth = aggState.drilldownPath.length;
    const nextGroupBySpec = aggState.groupBy[depth + 1];
    const aggregates = [...aggState.aggregates];
    if (nextGroupBySpec) {
      const subgroupAlias = `${nextGroupBySpec.key}_count`;
      if (!aggregates.some((a) => a.alias === subgroupAlias)) {
        const autoAgg: AggregateSpec = {
          function: 'COUNT',
          key: nextGroupBySpec.key,
          distinct: true,
          alias: subgroupAlias,
          ...(nextGroupBySpec.target ? { target: nextGroupBySpec.target } : {}),
        };
        if (nextGroupBySpec.truncate) autoAgg.truncate = nextGroupBySpec.truncate;
        aggregates.push(autoAgg);
      }
    }

    return {
      groups,
      pagination: aggState.pagination,
      ordering: aggState.ordering.length > 0 ? aggState.ordering : undefined,
      group_by: [aggState.groupBy[depth]],
      aggregates,
      having: aggState.having.length > 0 ? aggState.having : undefined,
    };
  },
);

export const selectLeafFilterGroups = createSelector(
  selectAggregateState,
  selectFilter,
  (aggState, filter) => {
    const groups = buildAlertFilterGroups(filter);
    groups.push(...buildDrilldownGroups(aggState.drilldownPath));
    return { groups, ordering: filter.ordering ?? [] };
  },
);
